// skipcq: JS-W1028, JS-0128
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CardContent,
  CardActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useCrawlDispatcherRunsChart from '../../hooks/useCrawlDispatcherRunsChart';
import useSchedulerDashboardSummary from '../../hooks/useSchedulerDashboardSummary';
import DashboardSurfaceCard from './DashboardSurfaceCard';

const DISPATCHER_CHART_HEIGHT_PX = 200;

function formatDispatcherTooltipValue(value, name) {
  return [Number(value).toLocaleString(), name];
}

const statColumn = {
  flex: '1 1 0',
  minWidth: 0,
  textAlign: 'center',
  px: 0.5,
};

function SchedulerStatsRow({ runsSucceeded, runsFailed }) {
  const succeededParsed = Number(runsSucceeded);
  const failedParsed = Number(runsFailed);
  const succeeded = Number.isFinite(succeededParsed) ? succeededParsed : 0;
  const failed = Number.isFinite(failedParsed) ? failedParsed : 0;
  const completed = succeeded + failed;
  const failureRatePercent =
    completed > 0 ? Math.round((failed / completed) * 1000) / 10 : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        alignItems: 'flex-start',
      }}
    >
      <Box sx={statColumn}>
        <Typography variant="h5" component="div" color="success.main">
          {succeeded.toLocaleString()}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Succeeded
        </Typography>
      </Box>
      <Box sx={statColumn}>
        <Typography variant="h5" component="div" color="error">
          {failed.toLocaleString()}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Failed
        </Typography>
      </Box>
      <Box sx={statColumn}>
        <Typography variant="h5" component="div" sx={{ color: 'text.primary' }}>
          {failureRatePercent === null ? '—' : `${failureRatePercent}%`}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Failure rate
        </Typography>
      </Box>
    </Box>
  );
}

function CrawlDispatcherRunsChartSection({ children }) {
  return (
    <Box
      sx={{
        mt: 2,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {children}
    </Box>
  );
}

function CrawlDispatcherRunsChart() {
  const theme = useTheme();
  const { rows, loading, error } = useCrawlDispatcherRunsChart();
  const tooltipContentStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      color: theme.palette.text.primary,
    }),
    [theme],
  );

  if (loading) {
    return (
      <CrawlDispatcherRunsChartSection>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 48,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </CrawlDispatcherRunsChartSection>
    );
  }
  if (error) {
    return (
      <CrawlDispatcherRunsChartSection>
        <Typography variant="body2" color="error">
          Error loading dispatcher runs
        </Typography>
      </CrawlDispatcherRunsChartSection>
    );
  }
  if (rows.length === 0) {
    return (
      <CrawlDispatcherRunsChartSection>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No dispatcher run history yet
        </Typography>
      </CrawlDispatcherRunsChartSection>
    );
  }

  return (
    <CrawlDispatcherRunsChartSection>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        Last 24 dispatcher runs
      </Typography>
      <Box sx={{ width: '100%', height: DISPATCHER_CHART_HEIGHT_PX }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            margin={{ top: 4, right: 8, left: 4, bottom: 0 }}
          >
            <XAxis
              dataKey="label"
              height={64}
              interval={1}
              angle={-32}
              textAnchor="end"
              tick={{ fontSize: 10 }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              allowDecimals={false}
              width={40}
            />
            <Tooltip
              contentStyle={tooltipContentStyle}
              formatter={formatDispatcherTooltipValue}
            />
            <Bar
              name="Dispatched"
              dataKey="dispatchedCount"
              stackId="dispatcher"
              fill={theme.palette.success.main}
            />
            <Bar
              name="Enqueue errors"
              dataKey="enqueueErrors"
              stackId="dispatcher"
              fill={theme.palette.error.main}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </CrawlDispatcherRunsChartSection>
  );
}

function SchedulerSummaryCard() {
  const { stats, loading, error } = useSchedulerDashboardSummary();

  return (
    <DashboardSurfaceCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Crawl scheduler
        </Typography>
        <Typography
          variant="body2"
          paragraph
          sx={{
            color: 'text.secondary',
          }}
        >
          Scheduled crawler runs (totals)
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            Error loading scheduler stats
          </Typography>
        ) : (
          <>
            <SchedulerStatsRow
              runsSucceeded={stats.runsSucceeded}
              runsFailed={stats.runsFailed}
            />
            <CrawlDispatcherRunsChart />
          </>
        )}
      </CardContent>
      <CardActions>
        <Button variant="outlined" component={Link} to="/crawler-scheduler" fullWidth>
          Manage schedules
        </Button>
      </CardActions>
    </DashboardSurfaceCard>
  );
}

export default SchedulerSummaryCard;
